import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  ArrowLeft,
  Download,
  Sparkles,
  Target,
  TrendingUp,
  ShieldCheck,
  MessageCircle,
  Users,
  Zap,
  AlertTriangle,
  ChevronRight,
  Copy,
  CheckCircle2,
  ScanEye,
} from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken } from "@/lib/api";
import type { DiscoveryReport } from "@/lib/discovery-types";
import { DashboardLayout } from "@/components/layout/DashboardLayout";
import { EmptyState } from "@/components/shared/EmptyState";
import { Button } from "@/components/ui/button";

const VELOCITY_COLOR: Record<string, string> = {
  rising: "bg-violet-50 text-violet-700 border-violet-200",
  emerging: "bg-blue-50 text-blue-700 border-blue-200",
  stable: "bg-slate-100 text-slate-700 border-slate-200",
  saturated: "bg-amber-50 text-amber-800 border-amber-200",
  declining: "bg-rose-50 text-rose-700 border-rose-200",
};

const CLUSTER_LABELS: Record<string, { label: string; color: string }> = {
  pricing: { label: "Pricing", color: "bg-emerald-50 border-emerald-200 text-emerald-700" },
  trust: { label: "Trust", color: "bg-rose-50 border-rose-200 text-rose-700" },
  quality: { label: "Quality", color: "bg-amber-50 border-amber-200 text-amber-800" },
  delivery: { label: "Delivery", color: "bg-orange-50 border-orange-200 text-orange-700" },
  support: { label: "Support", color: "bg-blue-50 border-blue-200 text-blue-700" },
  education: { label: "Education gaps", color: "bg-violet-50 border-violet-200 text-violet-700" },
  features: { label: "Feature limits", color: "bg-slate-100 border-slate-200 text-slate-700" },
  comparison: { label: "Comparison", color: "bg-fuchsia-50 border-fuchsia-200 text-fuchsia-700" },
  expectations: { label: "Unmet expectations", color: "bg-red-50 border-red-200 text-red-700" },
};

const ScoreCard: React.FC<{
  label: string;
  value: number;
  hint: string;
  tone: "violet" | "green" | "amber";
}> = ({ label, value, hint, tone }) => {
  const toneClass = {
    violet: "text-[color:var(--sg-violet)]",
    green: "text-[color:var(--sg-success)]",
    amber: "text-[color:var(--sg-warning)]",
  }[tone];
  return (
    <div className="sg-card p-5">
      <div className="sg-label">{label}</div>
      <div className={`sg-stat-num mt-3 ${toneClass}`}>{value}</div>
      <div className="mt-1 text-xs text-[color:var(--sg-muted)]">{hint}</div>
    </div>
  );
};

const Section: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  testId?: string;
}> = ({ icon, title, subtitle, children, testId }) => (
  <div className="sg-card p-6" data-testid={testId}>
    <div className="flex items-start gap-3 mb-4">
      <div className="h-9 w-9 rounded-lg bg-[color:var(--sg-muted-bg)] flex items-center justify-center shrink-0">
        {icon}
      </div>
      <div>
        <div className="font-display text-lg font-semibold tracking-tight">{title}</div>
        {subtitle && <div className="text-xs text-[color:var(--sg-muted)] mt-0.5">{subtitle}</div>}
      </div>
    </div>
    {children}
  </div>
);

const CopyPhrase: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  return (
    <button
      onClick={() => {
        navigator.clipboard.writeText(text);
        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      }}
      className="text-left w-full px-3 py-2 rounded border border-[color:var(--sg-border)] hover:border-[color:var(--sg-fg)] bg-white hover:bg-[color:var(--sg-muted-bg)] transition-colors text-sm flex items-center gap-2 group"
    >
      <span className="flex-1">{text}</span>
      {copied ? (
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600 shrink-0" />
      ) : (
        <Copy className="h-3.5 w-3.5 text-[color:var(--sg-muted)] opacity-0 group-hover:opacity-100 shrink-0 transition-opacity" />
      )}
    </button>
  );
};

export const DiscoveryReportPage: React.FC = () => {
  const { reportId } = useParams<{ reportId: string }>();
  const report = useQuery<DiscoveryReport>({
    queryKey: ["discovery-report", reportId],
    queryFn: async () => (await api.get(`/discovery/report/${reportId}`)).data,
    enabled: !!reportId,
  });

  const exportCsv = () => {
    const url = `${API_BASE}/discovery/report/${reportId}/export.csv`;
    fetch(url, { headers: { Authorization: `Bearer ${getToken() || ""}` } })
      .then((r) => r.blob())
      .then((blob) => {
        const link = document.createElement("a");
        link.href = URL.createObjectURL(blob);
        link.download = `discovery_${reportId}.csv`;
        link.click();
      })
      .catch(() => toast.error("Export failed"));
  };

  if (report.isLoading) {
    return (
      <DashboardLayout title="Loading report…">
        <div className="sg-skeleton h-96" />
      </DashboardLayout>
    );
  }
  const r = report.data;
  if (!r) {
    return (
      <DashboardLayout title="Report not found">
        <EmptyState title="Report not found" description="It may have been deleted." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title={r.query_text} subtitle={`Discovery report · ${r.region} · last ${r.date_range_days} days`} seoDescription={`Discovery report for "${r.query_text}" — demand themes, pain points, buying intent (${r.region}, last ${r.date_range_days} days).`} seoPath={`/discovery/report/${r.report_id}`}>
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <Link
          to="/discovery"
          className="text-sm text-[color:var(--sg-muted)] hover:text-[color:var(--sg-fg)] inline-flex items-center gap-1"
        >
          <ArrowLeft className="h-3.5 w-3.5" /> Back to Discovery
        </Link>
        <div className="flex items-center gap-2">
          <span className={`sg-chip ${VELOCITY_COLOR[r.trend_velocity] || ""} font-semibold uppercase`}>
            <TrendingUp className="h-3 w-3" /> {r.trend_velocity}
          </span>
          <Button
            variant="outline"
            size="sm"
            data-testid="export-csv"
            onClick={exportCsv}
          >
            <Download className="h-3.5 w-3.5 mr-1.5" /> Export CSV
          </Button>
        </div>
      </div>

      {/* Score row */}
      <div className="grid sm:grid-cols-3 gap-4 mb-6" data-testid="score-row">
        <ScoreCard
          label="Idea validation"
          value={r.idea_validation_score}
          hint="Curiosity × problem intensity × buying intent"
          tone="violet"
        />
        <ScoreCard
          label="Confidence"
          value={r.confidence_score}
          hint={`${r.source_item_count} signals · ${r.selected_sources.length} sources`}
          tone="green"
        />
        <ScoreCard
          label="Trend velocity"
          value={
            r.trend_velocity === "rising" ? 90 :
              r.trend_velocity === "emerging" ? 70 :
              r.trend_velocity === "stable" ? 50 :
              r.trend_velocity === "saturated" ? 30 : 15
          }
          hint={r.trend_velocity}
          tone="amber"
        />
      </div>

      {/* Opportunity summary */}
      <div className="sg-card p-6 mb-6 relative overflow-hidden" data-testid="opportunity-summary">
        <div className="sg-grain" />
        <div className="relative">
          <div className="flex items-center gap-2 mb-2">
            <Sparkles className="h-5 w-5 text-[color:var(--sg-violet)]" />
            <div className="sg-label">Opportunity summary</div>
          </div>
          <p className="font-display text-xl leading-snug tracking-tight text-[color:var(--sg-fg)] mb-4">
            {r.summary?.recommendation}
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3 text-sm">
            <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">Top demand</div><div>{r.summary?.top_demand}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">Top pain</div><div>{r.summary?.top_pain}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">Top objection</div><div>{r.summary?.top_objection}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">Top signal</div><div>{r.summary?.top_signal}</div></div>
            <div><div className="text-[10px] uppercase tracking-widest text-[color:var(--sg-muted)] mb-1">Top opportunity</div><div>{r.summary?.top_opportunity}</div></div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Section
          icon={<Target className="h-4 w-4 text-[color:var(--sg-violet)]" />}
          title="Demand themes"
          subtitle="What people are talking about, most to least"
          testId="section-themes"
        >
          {r.demand_themes.length > 0 ? (
            <ul className="space-y-3">
              {r.demand_themes.map((t, i) => (
                <li key={i} className="border-l-2 border-[color:var(--sg-violet)] pl-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-semibold">{t.theme}</span>
                    <span className="text-xs text-[color:var(--sg-muted)]">{t.mention_count} mentions</span>
                  </div>
                  {t.sample_quotes?.slice(0, 2).map((q, j) => (
                    <p key={j} className="text-xs text-[color:var(--sg-muted)] mt-1 italic">"{q}"</p>
                  ))}
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[color:var(--sg-muted)]">No themes detected.</div>
          )}
        </Section>

        <Section
          icon={<Zap className="h-4 w-4 text-[color:var(--sg-violet)]" />}
          title="Buying-intent signals"
          subtitle="Direct purchase-ready phrases from the wild"
          testId="section-signals"
        >
          {r.buying_intent_signals.length > 0 ? (
            <ul className="space-y-2">
              {r.buying_intent_signals.map((s, i) => (
                <li key={i} className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-sm">"{s.phrase}"</div>
                    <div className="text-xs text-[color:var(--sg-muted)] mt-0.5 capitalize">{s.source}</div>
                  </div>
                  <div className="sg-chip bg-violet-50 text-violet-700 border-violet-200 shrink-0">
                    {Math.round((s.intent_score || 0) * 100)}%
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[color:var(--sg-muted)]">No signals detected yet.</div>
          )}
        </Section>

        <Section
          icon={<AlertTriangle className="h-4 w-4 text-rose-600" />}
          title="Pain-point clusters"
          subtitle="Grouped complaints and frustrations"
          testId="section-pain"
        >
          <div className="space-y-2">
            {Object.entries(r.pain_point_clusters || {}).map(([k, list]) => (
              <div key={k}>
                <div className="flex items-center justify-between text-xs mb-1">
                  <span className={`sg-chip ${CLUSTER_LABELS[k]?.color || "bg-slate-100"}`}>
                    {CLUSTER_LABELS[k]?.label || k}
                  </span>
                  <span className="text-[color:var(--sg-muted)]">{list.length}</span>
                </div>
                {list.length > 0 && (
                  <ul className="text-xs text-[color:var(--sg-fg)] space-y-1 ml-3 mb-2">
                    {list.map((issue, i) => (
                      <li key={i} className="leading-relaxed">· {issue}</li>
                    ))}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </Section>

        <Section
          icon={<MessageCircle className="h-4 w-4 text-blue-600" />}
          title="Customer language bank"
          subtitle="Verbatim phrases for content, ads and captions"
          testId="section-language"
        >
          {r.language_bank.length > 0 ? (
            <div className="space-y-2">
              {r.language_bank.map((p, i) => (
                <CopyPhrase key={i} text={p} />
              ))}
            </div>
          ) : (
            <div className="text-sm text-[color:var(--sg-muted)]">No phrases mined yet.</div>
          )}
        </Section>

        <Section
          icon={<ScanEye className="h-4 w-4 text-[color:var(--sg-violet)]" />}
          title="Repeated questions"
          subtitle="Convert these directly into FAQ or reels"
          testId="section-questions"
        >
          {r.repeated_questions.length > 0 ? (
            <ul className="space-y-2">
              {r.repeated_questions.map((q, i) => (
                <li key={i} className="flex items-start justify-between gap-3">
                  <span className="text-sm">{q.question}</span>
                  <span className="text-xs text-[color:var(--sg-muted)] shrink-0">×{q.count}</span>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[color:var(--sg-muted)]">No repeated questions found.</div>
          )}
        </Section>

        <Section
          icon={<ShieldCheck className="h-4 w-4 text-emerald-600" />}
          title="Competitor gaps"
          subtitle="What existing options do / don't offer"
          testId="section-competitor"
        >
          <div className="grid grid-cols-3 gap-3 text-xs">
            <div>
              <div className="font-semibold text-emerald-700 mb-1.5">Liked</div>
              <ul className="space-y-1">
                {r.competitor_gaps.liked?.length ? r.competitor_gaps.liked.map((v, i) => <li key={i}>· {v}</li>) : <li className="text-[color:var(--sg-muted)]">—</li>}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-rose-700 mb-1.5">Disliked</div>
              <ul className="space-y-1">
                {r.competitor_gaps.disliked?.length ? r.competitor_gaps.disliked.map((v, i) => <li key={i}>· {v}</li>) : <li className="text-[color:var(--sg-muted)]">—</li>}
              </ul>
            </div>
            <div>
              <div className="font-semibold text-[color:var(--sg-violet)] mb-1.5">Missing</div>
              <ul className="space-y-1">
                {r.competitor_gaps.missing?.length ? r.competitor_gaps.missing.map((v, i) => <li key={i}>· {v}</li>) : <li className="text-[color:var(--sg-muted)]">—</li>}
              </ul>
            </div>
          </div>
        </Section>

        <Section
          icon={<Sparkles className="h-4 w-4 text-[color:var(--sg-violet)]" />}
          title="Content opportunities"
          subtitle="Concrete ideas ready to publish"
          testId="section-content"
        >
          {r.content_opportunities.length > 0 ? (
            <ul className="space-y-3">
              {r.content_opportunities.map((c, i) => (
                <li key={i} className="border-l-2 border-[color:var(--sg-violet)] pl-3">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="sg-chip bg-violet-50 text-violet-700 border-violet-200">{c.type}</span>
                  </div>
                  <div className="text-sm font-semibold">{c.idea}</div>
                  <p className="text-xs text-[color:var(--sg-muted)] mt-1 italic">Hook: "{c.hook}"</p>
                </li>
              ))}
            </ul>
          ) : (
            <div className="text-sm text-[color:var(--sg-muted)]">No content ideas generated.</div>
          )}
        </Section>

        <Section
          icon={<Users className="h-4 w-4 text-blue-600" />}
          title="Niche opportunities + personas"
          subtitle="Underserved segments and audience patterns"
          testId="section-persona"
        >
          <div className="mb-3">
            <div className="text-xs uppercase tracking-wider text-[color:var(--sg-muted)] mb-2">Persona hints</div>
            <div className="flex flex-wrap gap-1.5">
              {r.persona_hints.map((p, i) => (
                <span key={i} className="sg-chip bg-blue-50 text-blue-700 border-blue-200">{p}</span>
              ))}
              {r.persona_hints.length === 0 && <span className="text-xs text-[color:var(--sg-muted)]">—</span>}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-[color:var(--sg-muted)] mb-2">Niche opportunities</div>
            <ul className="space-y-1 text-sm">
              {r.niche_opportunities.map((n, i) => <li key={i}>· {n}</li>)}
              {r.niche_opportunities.length === 0 && <li className="text-[color:var(--sg-muted)]">—</li>}
            </ul>
          </div>
        </Section>
      </div>

      {r.source_quality.length > 0 && (
        <div className="sg-card p-6 mt-6" data-testid="source-quality">
          <div className="sg-label mb-1">Source quality scoring</div>
          <div className="font-display text-lg font-semibold tracking-tight mb-4">
            Where the strongest signals came from
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase tracking-wider text-[color:var(--sg-muted)] border-b border-[color:var(--sg-border)]">
                  <th className="pb-2">Source</th>
                  <th className="pb-2">Relevance</th>
                  <th className="pb-2">Buyer intent</th>
                  <th className="pb-2">Quality</th>
                  <th className="pb-2">Freshness</th>
                </tr>
              </thead>
              <tbody>
                {r.source_quality.map((s, i) => (
                  <tr key={i} className="border-b border-[color:var(--sg-border)] last:border-0">
                    <td className="py-2 font-medium capitalize">{s.source}</td>
                    <td className="py-2">{s.relevance}</td>
                    <td className="py-2">{s.buyer_intent}</td>
                    <td className="py-2">{s.quality}</td>
                    <td className="py-2">{s.freshness}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
};

export default DiscoveryReportPage;
